import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { LoginCredentials, FormErrors } from '../../types';
import './Login.css';

const Login: React.FC = () => {
  const { login, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: '',
    password: '',
    rememberMe: false
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string>('');

  // 如果已經登入，重定向到儀表板
  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // 表單驗證
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!credentials.email.trim()) {
      newErrors.email = '請輸入電子郵件';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(credentials.email)) {
      newErrors.email = '請輸入有效的電子郵件格式';
    }

    if (!credentials.password) {
      newErrors.password = '請輸入密碼';
    } else if (credentials.password.length < 6) {
      newErrors.password = '密碼至少需要6個字符';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 處理輸入變化
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    setCredentials(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // 清除對應的錯誤信息
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // 清除登入錯誤信息
    if (loginError) {
      setLoginError('');
    }
  };

  // 處理表單提交
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setLoginError('');

    try {
      const response = await login(credentials);
      
      if (!response.success) {
        setLoginError(response.message || '登入失敗，請檢查用戶名和密碼');
      }
    } catch (error) {
      setLoginError('登入失敗，請稍後再試');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 處理鍵盤事件
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit(e as any);
    }
  };

  if (isLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">載入中...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <div className="login-page">
      <Container>
        <Row className="justify-content-center align-items-center min-vh-100">
          <Col xs={12} sm={8} md={6} lg={4}>
            <Card className="login-card shadow">
              <Card.Body className="p-4">
                <div className="text-center mb-4">
                  <i className="fas fa-parking login-icon"></i>
                  <h2 className="login-title">停車管理系統</h2>
                  <p className="text-muted">請登入您的帳戶</p>
                </div>

                {loginError && (
                  <Alert variant="danger" className="mb-3" role="alert">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {loginError}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit} noValidate>
                  <Form.Group className="mb-3">
                    <Form.Label htmlFor="email">
                      電子郵件 <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      id="email"
                      name="email"
                      type="email"
                      value={credentials.email}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      isInvalid={!!errors.email}
                      placeholder="請輸入電子郵件"
                      autoComplete="email"
                      autoFocus
                      aria-describedby={errors.email ? 'email-error' : undefined}
                    />
                    <Form.Control.Feedback type="invalid" id="email-error">
                      {errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label htmlFor="password">
                      密碼 <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="password-input-wrapper">
                      <Form.Control
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={credentials.password}
                        onChange={handleInputChange}
                        onKeyPress={handleKeyPress}
                        isInvalid={!!errors.password}
                        placeholder="請輸入密碼"
                        autoComplete="current-password"
                        aria-describedby={errors.password ? 'password-error' : undefined}
                      />
                      <Button
                        variant="outline-secondary"
                        className="password-toggle-btn"
                        onClick={() => setShowPassword(!showPassword)}
                        type="button"
                        aria-label={showPassword ? '隱藏密碼' : '顯示密碼'}
                      >
                        <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </Button>
                    </div>
                    <Form.Control.Feedback type="invalid" id="password-error">
                      {errors.password}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Check
                      id="rememberMe"
                      name="rememberMe"
                      type="checkbox"
                      label="記住我"
                      checked={credentials.rememberMe}
                      onChange={handleInputChange}
                    />
                  </Form.Group>

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-100 login-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        登入中...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-sign-in-alt me-2"></i>
                        登入
                      </>
                    )}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Login;